import { ShoppingCart, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

const getUrgencyBadge = (urgency) => {
  const styles = {
    critical: 'bg-red-600 text-white',
    high: 'bg-orange-600 text-white',
    medium: 'bg-yellow-600 text-white',
    low: 'bg-green-600 text-white'
  };
  return (
    <span className={`px-2 py-1 text-xs font-bold uppercase rounded-sm ${styles[urgency] || styles.medium}`}>
      {urgency}
    </span>
  );
};

const ReorderSuggestions = ({ suggestions, onGeneratePO }) => {
  if (suggestions.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-sm mb-8">
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900 uppercase tracking-wider" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Smart Reorder Suggestions
            </h2>
            <p className="text-xs text-slate-500 mt-1">AI-powered recommendations based on consumption patterns</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => onGeneratePO(suggestions, 'reorder')}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="generate-po-reorder-btn"
            >
              <FileText size={16} className="mr-2" />
              Generate PO
            </Button>
            <ShoppingCart className="text-orange-500" size={24} />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Part Number</th>
              <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Item</th>
              <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Brand</th>
              <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Current Stock</th>
              <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Avg Consumption</th>
              <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Days Left</th>
              <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Recommended Order</th>
              <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Urgency</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {suggestions.map((suggestion) => (
              <tr
                key={suggestion.item_id}
                data-testid={`reorder-suggestion-${suggestion.part_number}`}
                className={`hover:bg-orange-50/50 transition-colors ${
                  suggestion.urgency === 'critical' ? 'bg-red-50' : 
                  suggestion.urgency === 'high' ? 'bg-orange-50' : 'bg-yellow-50'
                }`}
              >
                <td className="px-4 py-4 font-medium text-slate-900">{suggestion.part_number}</td>
                <td className="px-4 py-4">
                  <div>
                    <p className="font-semibold text-slate-900">{suggestion.item_name}</p>
                    {suggestion.location && <p className="text-xs text-slate-500">{suggestion.location}</p>}
                  </div>
                </td>
                <td className="px-4 py-4 text-slate-700">{suggestion.brand || '-'}</td>
                <td className="px-4 py-4 text-right">
                  <span className={`font-bold ${suggestion.current_quantity === 0 ? 'text-red-600' : 'text-slate-900'}`}>
                    {suggestion.current_quantity}
                  </span>
                </td>
                <td className="px-4 py-4 text-right text-slate-700">{suggestion.avg_daily_consumption.toFixed(1)}/day</td>
                <td className="px-4 py-4 text-right">
                  {suggestion.days_until_stockout > 0 ? (
                    <span className={`font-semibold ${
                      suggestion.days_until_stockout <= 7 ? 'text-red-600' : 
                      suggestion.days_until_stockout <= 14 ? 'text-orange-600' : 'text-slate-900'
                    }`}>
                      {Math.ceil(suggestion.days_until_stockout)} days
                    </span>
                  ) : (
                    <span className="font-bold text-red-600">OUT OF STOCK</span>
                  )}
                </td>
                <td className="px-4 py-4 text-right">
                  <span className="font-bold text-orange-600 text-base">{suggestion.recommended_order_quantity}</span>
                </td>
                <td className="px-4 py-4 text-center">{getUrgencyBadge(suggestion.urgency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export { ReorderSuggestions, getUrgencyBadge };
